import { SearchInput, Typography } from "@/components";
import { BackAppBar } from "@/components/ui";
import { ExternalInvitations } from "@/components/ui/invitations";
import { QUERY_KEYS } from "@/libraries/constant";
import { isAxiosError, isMpisAccount, pluralize } from "@/libraries/helper";
import {
	getCaseQueryOptions,
	getMembersQueryOptions,
	getMpisMembersQueryOptions,
	getTeamsQueryOptions,
} from "@/queries";
import { InviteCaseMemberSearchParamSchema, MESSAGE_TAG } from "@/schemas";
import type { CaseTeam, ContactMember } from "@/types";
import Button from "@mui/material/Button";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import { onlineManager, useSuspenseQuery } from "@tanstack/react-query";
import { ErrorComponent, createFileRoute } from "@tanstack/react-router";
import { zodSearchValidator } from "@tanstack/router-zod-adapter";
import omit from "lodash/omit";
import { useCallback, useState } from "react";
import ForbiddenAccessMembers from "./-components/ForbiddenAccessMembers";
import PendingComponentMembers from "./-components/PendingComponentMembers";

export const Route = createFileRoute(
	"/dashboard/case/view/$id/members/external",
)({
	validateSearch: zodSearchValidator(InviteCaseMemberSearchParamSchema),
	loaderDeps: ({ search }) => ({
		tab: search.tab,
		subTab: search.subtab,
	}),
	loader: ({ context, params, deps }) => {
		if (onlineManager.isOnline() && !context.isRestoring) {
			return [
				context.queryClient.ensureQueryData(getCaseQueryOptions(params.id)),
				...(deps.tab !== "mpis"
					? [
							context.queryClient.ensureInfiniteQueryData(
								getMembersQueryOptions({
									directory: deps.tab === "directory",
								}),
							),
							context.queryClient.ensureInfiniteQueryData(
								getTeamsQueryOptions({
									directory: deps.tab === "directory",
									scope: "case_teams",
								}),
							),
						]
					: [
							context.queryClient.ensureInfiniteQueryData(
								getMpisMembersQueryOptions(),
							),
						]),
			];
		}
		return [
			context.queryClient.getQueryData([QUERY_KEYS.GET_CASE, params.id]),
			...(deps.tab !== "mpis"
				? [
						context.queryClient.getQueryData([
							QUERY_KEYS.GET_MEMBERS,
							{
								directory: deps.tab === "directory",
							},
						]),
						context.queryClient.getQueryData([
							QUERY_KEYS.GET_TEAMS,
							{
								directory: deps.tab === "directory",
								scope: "case_teams",
							},
						]),
					]
				: [context.queryClient.getQueryData([QUERY_KEYS.GET_MPIS_MEMBERS])]),
		];
	},
	component: RouteComponent,
	pendingComponent: () => (
		<PendingComponentMembers title="Invite Member External" />
	),
	errorComponent: ({ error }) => {
		if (error && isAxiosError(error, 403)) {
			return <ForbiddenAccessMembers title="Invite Member External" />;
		}
		return <ErrorComponent error={error} />;
	},
});

function RouteComponent() {
	const navigate = Route.useNavigate();
	const tags = Route.useSearch({
		select: (s) => s.tags || MESSAGE_TAG.REFERAL,
	});
	const search = Route.useSearch({
		select: (s) => s.search || "",
	});
	const caseId = Route.useParams({
		select: (s) => s.id,
	});
	const tab = Route.useLoaderDeps({
		select: (s) => s.tab as "mpis" | "contact" | "directory",
	});
	const subTab = Route.useLoaderDeps({
		select: (s) => s.subTab as "team" | "contact",
	});

	const caseQuery = useSuspenseQuery(getCaseQueryOptions(caseId as number));

	const [selectedMember, setSelectedMember] = useState<ContactMember | null>(
		null,
	);
	const [selectedTeam, setSelectedTeam] = useState<CaseTeam | null>(null);

	const showSubmit = !!selectedMember || !!selectedTeam;

	const handleChangeTab = (e: React.SyntheticEvent, newValue: string) => {
		e.preventDefault();
		return navigate({
			search: {
				tab: newValue,
				subtab: newValue === "mpis" ? "contact" : undefined,
			},
			replace: true,
		});
	};

	const handleChangeSubTab = (e: React.SyntheticEvent, newValue: string) => {
		e.preventDefault();
		return navigate({
			search: {
				tab: tab,
				subtab: newValue,
			},
			replace: true,
		});
	};

	const handleOnChange = useCallback(
		(
			e: React.ChangeEvent<HTMLInputElement>,
			val: any,
			type: "teams" | "contacts",
		) => {
			e.preventDefault();
			const operator = type === "teams" ? setSelectedTeam : setSelectedMember;
			const resetOperator =
				type === "teams" ? setSelectedMember : setSelectedTeam;
			resetOperator(null);
			return operator(val);
		},
		[],
	);

	const handleOnNext = useCallback(
		(e: React.MouseEvent<HTMLButtonElement>) => {
			e.preventDefault();
			return navigate({
				replace: true,
				to: "/dashboard/case/view/$id",
				params: {
					id: caseId,
				},
				search: {
					tags: tags,
					tab: MESSAGE_TAG.EXTERNAL,
				},
				state: {
					selectedMember,
					selectedTeam,
				},
			});
		},
		[navigate, tags, caseId, selectedMember, selectedTeam],
	);

	return (
		<>
			<BackAppBar title="Invite Member External">
				<div className="container pb-2 space-y-4">
					<Tabs
						centered
						value={tab}
						variant="fullWidth"
						textColor="inherit"
						aria-label="Contact Type"
						className="!rounded-full !bg-gray-light [&_.MuiTabs-indicator]:!bg-transparent !p-1"
						onChange={handleChangeTab}
					>
						<Tab
							value="contact"
							label="Contacts"
							classes={{
								selected: "!bg-white !rounded-full",
							}}
						/>
						<Tab
							value="directory"
							label="Directory"
							classes={{
								selected: "!bg-white !rounded-full",
							}}
						/>
						{isMpisAccount() ? (
							<Tab
								value="mpis"
								label="MPIS"
								classes={{
									selected: "!bg-white !rounded-full",
								}}
							/>
						) : null}
					</Tabs>
					{tab !== "mpis" ? (
						<Tabs
							value={subTab}
							variant="fullWidth"
							textColor="inherit"
							aria-label="Contact Sub Type"
							className="[&_.MuiTabs-indicator]:bg-primary"
							onChange={handleChangeSubTab}
						>
							<Tab
								value="team"
								label="Teams"
								style={{ textTransform: "none" }}
							/>
							<Tab
								value="contact"
								label="Contacts"
								style={{ textTransform: "none" }}
							/>
						</Tabs>
					) : null}
					<SearchInput
						name="search"
						label="Search"
						placeholder="Search"
						value={search}
						onChange={(e) =>
							navigate({
								// @ts-expect-error
								search: (oldSearch) => ({
									...omit(oldSearch, ["search"]),
									search: e.target.value || undefined,
								}),
								replace: true,
							})
						}
					/>
				</div>
			</BackAppBar>
			<ExternalInvitations
				tab={tab}
				subTab={subTab}
				search={search}
				caseItem={caseQuery.data}
				selectedTeamId={selectedTeam?.id}
				selectedMemberId={selectedMember?.id || selectedMember?.aidbox_id}
				onChange={handleOnChange}
			/>
			{showSubmit ? (
				<div className="relative wrapper">
					<div className="relative container py-4 bg-white border-t border-gray-light">
						<div className="grid grid-cols-2 gap-2 justify-between items-center">
							<Typography fontWeight={500}>
								{pluralize({
									count: 1,
									singularText: selectedMember ? "Contact" : "Team",
								})}{" "}
								Selected
							</Typography>
							<Button
								type="submit"
								variant="contained"
								color="primary"
								size="large"
								className="!rounded-full"
								fullWidth
								onClick={handleOnNext}
							>
								Next
							</Button>
						</div>
					</div>
				</div>
			) : null}
		</>
	);
}
